'use client';
import React, { useState } from 'react';

interface EstimateForm {
  service: string;
  frequency: string;
  transactions: string;
  statements: string;
  addons: string[];
}

const serviceOptions = [
  { value: 'bookkeeping', label: 'Bookkeeping' },
  { value: 'reconciliation', label: 'Bank Reconciliation' },
  { value: 'financial-statements', label: 'Financial Statement Preparation' },
  { value: 'bundle', label: 'Full Bundle (All Three Services)' },
];

const frequencyOptions = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual / One-time' },
];

const transactionOptions = [
  { value: 'low', label: 'Under 50 transactions/month' },
  { value: 'medium', label: '50–150 transactions/month' },
  { value: 'high', label: '150–300 transactions/month' },
  { value: 'enterprise', label: '300+ transactions/month' },
];

const statementOptions = [
  { value: 'basic', label: 'Income Statement only' },
  { value: 'standard', label: 'Income Statement + Balance Sheet' },
  { value: 'full', label: 'Full Set (IS + BS + Cash Flow)' },
];

const addonOptions = [
  { value: 'catchup', label: 'Catch-up bookkeeping (prior months)' },
  { value: 'notes', label: 'Notes & disclosures' },
  { value: 'comparative', label: 'Comparative period analysis' },
  { value: 'rush', label: 'Rush delivery (under 5 business days)' },
];

function calculateEstimate(form: EstimateForm): { low: number; high: number; breakdown: string[] } {
  let low = 0;
  let high = 0;
  const breakdown: string[] = [];

  const freqMultiplier = form.frequency === 'monthly' ? 1 : form.frequency === 'quarterly' ? 0.8 : 0.7;

  if (form.service === 'bookkeeping' || form.service === 'bundle') {
    const txMap: Record<string, [number, number]> = {
      low: [80, 120],
      medium: [150, 220],
      high: [250, 380],
      enterprise: [400, 600],
    };
    const [l, h] = txMap[form.transactions] || [80, 120];
    low += l;
    high += h;
    breakdown.push(`Bookkeeping: $${l}–$${h}/mo`);
  }

  if (form.service === 'reconciliation' || form.service === 'bundle') {
    const txMap: Record<string, [number, number]> = {
      low: [50, 80],
      medium: [80, 130],
      high: [130, 200],
      enterprise: [200, 320],
    };
    const [l, h] = txMap[form.transactions] || [50, 80];
    low += l;
    high += h;
    breakdown.push(`Bank Reconciliation: $${l}–$${h}/mo`);
  }

  if (form.service === 'financial-statements' || form.service === 'bundle') {
    const stmtMap: Record<string, [number, number]> = {
      basic: [100, 160],
      standard: [160, 250],
      full: [250, 380],
    };
    const [l, h] = stmtMap[form.statements] || [100, 160];
    low += l;
    high += h;
    breakdown.push(`Financial Statements: $${l}–$${h}/mo`);
  }

  if (form.frequency !== 'monthly') {
    low = Math.round(low * freqMultiplier);
    high = Math.round(high * freqMultiplier);
    breakdown.push(`${form.frequency === 'quarterly' ? 'Quarterly' : 'Annual'} discount applied`);
  }

  if (form.addons.includes('catchup')) { low += 80; high += 150; breakdown.push('Catch-up: +$80–$150'); }
  if (form.addons.includes('notes')) { low += 40; high += 80; breakdown.push('Notes & disclosures: +$40–$80'); }
  if (form.addons.includes('comparative')) { low += 50; high += 90; breakdown.push('Comparative analysis: +$50–$90'); }
  if (form.addons.includes('rush')) { low = Math.round(low * 1.25); high = Math.round(high * 1.25); breakdown.push('Rush delivery: +25%'); }

  return { low, high, breakdown };
}

export default function EstimateSection() {
  const [form, setForm] = useState<EstimateForm>({
    service: '',
    frequency: 'monthly',
    transactions: 'low',
    statements: 'standard',
    addons: [],
  });
  const [showResult, setShowResult] = useState(false);

  const handleAddon = (value: string) => {
    setForm((prev) => ({
      ...prev,
      addons: prev.addons.includes(value)
        ? prev.addons.filter((a) => a !== value)
        : [...prev.addons, value],
    }));
    setShowResult(false);
  };

  const handleChange = (field: keyof EstimateForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setShowResult(false);
  };

  const estimate = form.service ? calculateEstimate(form) : null;

  return (
    <section id="estimate" className="bg-white py-16 sm:py-24 lg:py-32">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-24">
          {/* Left: Intro */}
          <div>
            <p
              className="text-primary font-sans text-xs font-semibold tracking-widest uppercase mb-4"
              style={{ letterSpacing: '3px' }}
            >
              Pricing Estimate
            </p>
            <h2
              className="mb-5 sm:mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(26px, 6vw, 44px)',
                fontWeight: 700,
                lineHeight: '1.15',
                letterSpacing: '-1px',
                color: '#111111',
              }}
            >
              Get a transparent
              <br />
              service estimate.
            </h2>
            <div className="w-12 h-0.5 bg-primary mb-6 sm:mb-8" />
            <p
              className="text-brand-grey700 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.75' }}
            >
              Use this calculator to get a ballpark estimate for your engagement. Final pricing is confirmed after reviewing your specific requirements through the intake form.
            </p>

            <div className="space-y-3 sm:space-y-4">
              {[
                { icon: '✓', text: 'No commitment required' },
                { icon: '✓', text: 'Estimates confirmed after intake review' },
                { icon: '✓', text: 'Itemized breakdown always provided' },
                { icon: '✓', text: 'Flexible monthly, quarterly, or one-time engagements' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <span className="text-primary font-bold text-sm mt-0.5 flex-shrink-0">{item.icon}</span>
                  <span
                    className="text-brand-grey700 text-sm"
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    {item.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Result display */}
            {estimate && (
              <div
                className="mt-8 sm:mt-10 p-6 sm:p-8 bg-brand-black text-white transition-all duration-500"
                style={{ transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }}
              >
                <p
                  className="text-primary text-xs font-semibold tracking-widest uppercase mb-3"
                  style={{ letterSpacing: '2px', fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                >
                  Estimated Range
                </p>
                <div
                  className="text-white mb-4"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: 'clamp(28px, 8vw, 40px)',
                    fontWeight: 700,
                    letterSpacing: '-1px',
                  }}
                >
                  ${estimate.low} – ${estimate.high}
                  <span
                    className="text-white/50 text-base font-normal ml-2"
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    /mo
                  </span>
                </div>
                <div className="space-y-1.5 border-t border-white/10 pt-4">
                  {estimate.breakdown.map((line) => (
                    <p
                      key={line}
                      className="text-white/60 text-sm"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                    >
                      {line}
                    </p>
                  ))}
                </div>
                <p
                  className="text-white/40 text-xs mt-4"
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                >
                  * Estimates are indicative. Final pricing confirmed after intake review.
                </p>
              </div>
            )}
          </div>

          {/* Right: Form */}
          <div className="bg-brand-grey50 p-5 sm:p-8 lg:p-10">
            <h3
              className="text-brand-dark mb-6 sm:mb-8"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: '20px',
                fontWeight: 600,
              }}
            >
              Configure your estimate
            </h3>

            {/* Service */}
            <div className="mb-5 sm:mb-6">
              <label
                className="label-field"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
              >
                Service needed <span className="text-primary">*</span>
              </label>
              <div className="space-y-2">
                {serviceOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-3 cursor-pointer border transition-all duration-200 touch-manipulation ${
                      form.service === opt.value
                        ? 'border-primary bg-white' : 'border-brand-grey200 bg-white hover:border-brand-grey300'
                    }`}
                    style={{ minHeight: '48px' }}
                  >
                    <input
                      type="radio"
                      name="service"
                      value={opt.value}
                      checked={form.service === opt.value}
                      onChange={() => handleChange('service', opt.value)}
                      className="accent-primary w-4 h-4 flex-shrink-0"
                    />
                    <span
                      className="text-sm text-brand-dark"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                    >
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Frequency */}
            <div className="mb-5 sm:mb-6">
              <label
                className="label-field"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
              >
                Engagement frequency
              </label>
              <div className="flex flex-wrap gap-2">
                {frequencyOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleChange('frequency', opt.value)}
                    className={`px-4 py-2.5 text-sm border transition-all duration-200 touch-manipulation ${
                      form.frequency === opt.value
                        ? 'bg-brand-black text-white border-brand-black' : 'bg-white text-brand-dark border-brand-grey200 hover:border-brand-grey500'
                    }`}
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '44px' }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Transaction volume */}
            <div className="mb-5 sm:mb-6">
              <label
                className="label-field"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
              >
                Monthly transaction volume
              </label>
              <select
                value={form.transactions}
                onChange={(e) => handleChange('transactions', e.target.value)}
                className="input-field"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', fontSize: '16px' }}
              >
                {transactionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Statement type (only if relevant) */}
            {(form.service === 'financial-statements' || form.service === 'bundle') && (
              <div className="mb-5 sm:mb-6">
                <label
                  className="label-field"
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                >
                  Statement package
                </label>
                <select
                  value={form.statements}
                  onChange={(e) => handleChange('statements', e.target.value)}
                  className="input-field"
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', fontSize: '16px' }}
                >
                  {statementOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Add-ons */}
            <div className="mb-6 sm:mb-8">
              <label
                className="label-field"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
              >
                Add-ons (optional)
              </label>
              <div className="space-y-2">
                {addonOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-3 cursor-pointer touch-manipulation py-1"
                    style={{ minHeight: '44px' }}
                  >
                    <input
                      type="checkbox"
                      checked={form.addons.includes(opt.value)}
                      onChange={() => handleAddon(opt.value)}
                      className="accent-primary w-5 h-5 flex-shrink-0"
                    />
                    <span
                      className="text-sm text-brand-dark"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                    >
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Calculate button */}
            <button
              onClick={() => setShowResult(true)}
              disabled={!form.service}
              className={`w-full py-4 font-bold text-base transition-all duration-300 touch-manipulation ${
                form.service
                  ? 'bg-primary text-white hover:bg-primary-dark cursor-pointer' : 'bg-brand-grey200 text-brand-grey500 cursor-not-allowed'
              }`}
              style={{
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
                minHeight: '52px',
              }}
            >
              Calculate Estimate
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
